import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  CalendarDays,
  Check,
  GitBranch,
  Globe2,
  GraduationCap,
  Headphones,
  MapPin,
  Video,
} from "lucide-react";
import SEO from "../components/SEO";
import WhatsAppButton from "../components/WhatsAppButton";
import { CONTACT, BRAND_NAME } from "../data/contactInfo";
import { PAGE_SEO, GLOBAL_ONLINE_REGIONS, SITE_NAME, getLocalBusinessSchema } from "../data/seoData";
import { SEO_IMAGES } from "../data/seoImages";
import { publicImage, CLASSES_IMAGES } from "../data/pageImages";
import instrumentsCatalog from "../data/instrumentsData";
import melams from "../data/melamsData";
import "../styles/ClassesPage.css";

const ENROLL_SEARCH = { pathname: "/contact", search: "?type=course&form=open" };
const GURU_IMAGE = `${process.env.PUBLIC_URL}/vineesh.png`;

const findInstrumentImage = (name) =>
  instrumentsCatalog.find((i) => i.name.toLowerCase().includes(name.toLowerCase()))?.image;

const ONLINE_FEATURES = [
  {
    icon: Video,
    title: "Live video classes",
    text: "Weekly live sessions with real-time correction on Veekku, Chaapu, talam, and melam patterns.",
  },
  {
    icon: Headphones,
    title: "Practice recordings",
    text: "Lesson recordings and drills — learn Chenda Melam online at your own pace between classes.",
  },
  {
    icon: Globe2,
    title: "Flexible time zones",
    text: "Morning and evening batches for USA, UK, UAE, Europe, and Asia-Pacific students.",
  },
];

const OFFLINE_FEATURES = [
  {
    icon: Building2,
    title: "Chennai academy",
    text: `Hands-on training in ${CONTACT.address.area} with live ensemble practice and gurukul-style instruction.`,
  },
  {
    icon: CalendarDays,
    title: "Weekend & evening",
    text: "Batches designed for working professionals, students, and children across Chennai.",
  },
  {
    icon: GitBranch,
    title: "Hybrid learning",
    text: "Combine offline sessions in India with online classes abroad — ideal for diaspora families.",
  },
];

const INSTRUMENTS = [
  {
    name: "Chenda",
    level: "Beginner → Advanced",
    desc: "Panchari, Pandi & classical melam forms — the core of Kerala percussion training.",
    image: findInstrumentImage("Chenda"),
  },
  {
    name: "Idakka",
    level: "Intermediate",
    desc: "Melodic temple rhythms & Sopana Sangeetham accompaniment.",
    image: findInstrumentImage("Idakka"),
  },
  {
    name: "Maddalam",
    level: "Intermediate → Advanced",
    desc: "Kathakali accompaniment & classical barrel-drum techniques.",
    image: findInstrumentImage("Maddalam"),
  },
  {
    name: "Thimila",
    level: "Advanced",
    desc: "Panchavadyam ensemble lead & temple festival rhythms.",
    image: findInstrumentImage("Thimila"),
  },
  {
    name: "Kombu",
    level: "Specialist",
    desc: "Traditional copper horn for Koodiyattam & classical arts.",
    image: findInstrumentImage("Kombu"),
  },
  {
    name: "Kurumkuzhal",
    level: "Specialist",
    desc: "Kerala wind instrument for folk & classical ensembles.",
    image: findInstrumentImage("Kurumkuzhal"),
  },
  {
    name: "Sopana Sangeetham",
    level: "Devotional path",
    desc: "Temple-step music tradition with Idakka accompaniment.",
    image: findInstrumentImage("Idakka"),
  },
  {
    name: "Thayambaka",
    level: "Advanced solo",
    desc: "Solo percussion repertoire for experienced Chenda students.",
    image: findInstrumentImage("Chenda"),
  },
];

const WHO_CAN_JOIN = [
  "Beginners worldwide — no prior percussion experience needed",
  "NRI and diaspora learners in USA, UK, UAE, Singapore, Malaysia, Europe",
  "Chennai students preferring offline hands-on training",
  "Performers upgrading to professional melam and Panchavadyam levels",
  "Families connecting children to Kerala heritage through music",
];

const EXPLORE_LINKS = [
  { to: "/courses", label: "All courses" },
  { to: "/melams", label: "Melam forms" },
  { to: "/chenda-melam-chennai", label: "Wedding Melam" },
  { to: "/testimonials", label: "Reviews" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

const ChendaClassesChennai = () => {
  const [activeInstrument, setActiveInstrument] = useState(0);
  const active = INSTRUMENTS[activeInstrument];

  return (
  <>
    <SEO
      title={PAGE_SEO.classes.title}
      description={PAGE_SEO.classes.description}
      path={PAGE_SEO.classes.path}
      ogImage={SEO_IMAGES.classes}
      jsonLd={getLocalBusinessSchema()}
    />

    <main className="learn-page">
      <header className="learn-hero">
        <img
          className="learn-hero__media"
          src={publicImage(CLASSES_IMAGES.hero)}
          alt={`Chenda Melam training session at ${CONTACT.address.area} academy, Chennai`}
          width={1600}
          height={900}
          fetchPriority="high"
        />
        <div className="learn-hero__veil" aria-hidden="true" />
        <div className="learn-hero__content">
          <p className="learn-hero__brand">{BRAND_NAME}</p>
          <h1 className="learn-hero__title">Chenda Melam Classes in Chennai</h1>
          <p className="learn-hero__lead">
            Learn Kerala style traditional Chenda Melam — online worldwide and offline in{" "}
            {CONTACT.address.area} — under Guru Thiruvambady Vineesh Maarar.
          </p>
          <div className="learn-hero__actions">
            <Link to="/courses" className="learn-btn learn-btn--primary">
              <GraduationCap size={18} aria-hidden="true" />
              View Courses
            </Link>
            <WhatsAppButton>WhatsApp Enquiry</WhatsAppButton>
          </div>
        </div>
      </header>

      <section className="learn-section learn-paths" aria-labelledby="learn-online-heading">
        <div className="learn-section__inner">
          <p className="learn-kicker">Online</p>
          <h2 id="learn-online-heading" className="learn-heading">
            Learn Chenda Melam globally
          </h2>
          <p className="learn-lead">
            Authentic Kerala percussion training over live video — students join {SITE_NAME} from
            across the world.
          </p>
          <ul className="learn-feature-list">
            {ONLINE_FEATURES.map(({ icon: Icon, title, text }) => (
              <li className="learn-feature" key={title}>
                <span className="learn-icon" aria-hidden="true">
                  <Icon size={22} strokeWidth={1.75} />
                </span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ul>
          <p className="learn-regions-label">Students join from</p>
          <ul className="learn-regions">
            {GLOBAL_ONLINE_REGIONS.map((region) => (
              <li key={region}>{region}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="learn-section learn-guru" aria-labelledby="learn-guru-heading">
        <div className="learn-section__inner learn-guru__inner">
          <figure className="learn-guru__figure">
            <img
              src={GURU_IMAGE}
              alt="Guru Thiruvambady Vineesh Maarar playing Chenda"
              width={819}
              height={1024}
              loading="lazy"
            />
          </figure>
          <div className="learn-guru__copy">
            <p className="learn-kicker">Our Guru</p>
            <h2 id="learn-guru-heading" className="learn-heading">
              Thiruvambady Vineesh Maarar
            </h2>
            <p className="learn-lead">
              Renowned Idakka specialist with 30+ years in temple music, Sopana Sangeetham, and
              Panchavadyam. Personal guidance from fundamentals to festival-stage performance.
            </p>
            <Link to="/courses" className="learn-text-link">
              Browse course catalog
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <section className="learn-section learn-offline" aria-labelledby="learn-offline-heading">
        <div className="learn-section__inner">
          <p className="learn-kicker">Offline</p>
          <h2 id="learn-offline-heading" className="learn-heading">
            Chennai &amp; Tamil Nadu classes
          </h2>
          <p className="learn-lead">
            Join offline Chenda Melam classes at our academy in {CONTACT.address.area} — hands-on learning with
            ensemble practice.
          </p>
          <ul className="learn-feature-list">
            {OFFLINE_FEATURES.map(({ icon: Icon, title, text }) => (
              <li className="learn-feature" key={title}>
                <span className="learn-icon" aria-hidden="true">
                  <Icon size={22} strokeWidth={1.75} />
                </span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ul>
          <p className="learn-studio">
            <MapPin size={16} aria-hidden="true" />
            {CONTACT.address.area}, Chennai · {CONTACT.address.line2}
          </p>
        </div>
      </section>

      <section className="learn-section learn-instruments" aria-labelledby="learn-instruments-heading">
        <div className="learn-section__inner">
          <p className="learn-kicker">Curriculum</p>
          <h2 id="learn-instruments-heading" className="learn-heading">
            Instruments &amp; courses
          </h2>
          <p className="learn-lead">
            Train in one or more Kerala percussion instruments — each with structured levels.
            Select an instrument to explore what you will learn.
          </p>

          <div className="learn-curric">
            <div className="learn-curric__stage" aria-live="polite">
              <div className="learn-curric__visual" key={active.name}>
                {active.image ? (
                  <img src={active.image} alt={active.name} />
                ) : null}
                <div className="learn-curric__visual-veil" aria-hidden="true" />
              </div>
              <div className="learn-curric__detail" key={`detail-${active.name}`}>
                <span className="learn-curric__level">{active.level}</span>
                <h3>{active.name}</h3>
                <p>{active.desc}</p>
                <Link to="/instruments" className="learn-text-link">
                  Explore all instruments
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
            </div>

            <ul className="learn-curric__rail" role="listbox" aria-label="Instruments you can learn">
              {INSTRUMENTS.map((item, index) => (
                <li key={item.name} role="option" aria-selected={index === activeInstrument}>
                  <button
                    type="button"
                    className={`learn-curric__tab${index === activeInstrument ? " is-active" : ""}`}
                    onClick={() => setActiveInstrument(index)}
                    onMouseEnter={() => setActiveInstrument(index)}
                  >
                    <span className="learn-curric__index" aria-hidden="true">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="learn-curric__name">{item.name}</span>
                    <span className="learn-curric__bar" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <Link to="/courses" className="learn-btn learn-btn--primary learn-btn--inline">
            See full course details
            <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="learn-section learn-melams" aria-labelledby="learn-melams-heading">
        <div className="learn-section__inner">
          <p className="learn-kicker">Forms</p>
          <h2 id="learn-melams-heading" className="learn-heading">
            Classical Melam forms you will learn
          </h2>
          <p className="learn-lead">
            Progressive curriculum covering essential Kerala melam traditions.
          </p>
          <div className="learn-melam-chips">
            {melams.map((m) => (
              <Link to={`/melams#${m.id}`} key={m.id} className="learn-melam-chip">
                {m.name}
              </Link>
            ))}
          </div>
          <Link to="/melams" className="learn-text-link">
            Read the full Melam guide
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="learn-section learn-who" aria-labelledby="learn-who-heading">
        <div className="learn-section__inner learn-section__inner--narrow">
          <p className="learn-kicker">Open to all</p>
          <h2 id="learn-who-heading" className="learn-heading">
            Who can join?
          </h2>
          <ul className="learn-checklist">
            {WHO_CAN_JOIN.map((item) => (
              <li key={item}>
                <Check size={18} strokeWidth={2.25} aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="learn-section learn-explore" aria-labelledby="learn-explore-heading">
        <div className="learn-section__inner">
          <p className="learn-kicker">Explore</p>
          <h2 id="learn-explore-heading" className="learn-heading">
            Keep learning with us
          </h2>
          <nav className="learn-explore__links" aria-label="Related pages">
            {EXPLORE_LINKS.map((link) => (
              <Link key={link.to} to={link.to}>
                {link.label}
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            ))}
          </nav>
        </div>
      </section>

      <section className="learn-cta-band" aria-labelledby="learn-cta-heading">
        <div className="learn-cta-band__inner">
          <h2 id="learn-cta-heading">Ready to start learning?</h2>
          <p>
            Enroll in a course or message us on WhatsApp for batch timings and fees.
          </p>
          <div className="learn-hero__actions">
            <Link to={ENROLL_SEARCH} className="learn-btn learn-btn--primary">
              Enroll Now
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <WhatsAppButton>WhatsApp {CONTACT.phone}</WhatsAppButton>
          </div>
        </div>
      </section>
    </main>
  </>
  );
};

export default ChendaClassesChennai;
